import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
  sub?: string;
  /** 指定するとドロップダウン内でグループ見出しとしてまとめて表示される */
  group?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
}

/**
 * 入力に応じて選択肢を部分一致で絞り込むコンボボックス。
 * 乗組員数が多い会社でも氏名の一部入力ですぐ絞り込める。
 */
const SearchableSelect: React.FC<Props> = ({ value, onChange, options, placeholder = '氏名で検索...', emptyText = '該当する候補がありません', disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.value === value);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const norm = (s: string) => s.toLowerCase();
  const filtered = query.trim()
    ? options.filter(o => norm(o.label + (o.sub ?? '')).includes(norm(query.trim())))
    : options;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} strokeWidth={2} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={open ? query : (selected?.label ?? '')}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
          placeholder={selected && !open ? selected.label : placeholder}
          style={{
            width: '100%', padding: '10px 30px 10px 30px', border: '1.5px solid #e2e8f0', borderRadius: '8px',
            fontSize: '13px', background: disabled ? '#f8fafc' : '#fff', boxSizing: 'border-box',
            cursor: disabled ? 'not-allowed' : 'text', color: '#0f172a',
          }}
        />
        <ChevronDown size={14} strokeWidth={2} color="#94a3b8" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
      </div>
      {open && !disabled && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff',
          border: '1.5px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 400, maxHeight: 260, overflowY: 'auto',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>{emptyText}</div>
          ) : filtered.map((o, i) => (
            <React.Fragment key={o.value}>
              {o.group !== undefined && (i === 0 || filtered[i - 1]!.group !== o.group) && (
                <div style={{
                  padding: '6px 12px 3px', fontSize: 10.5, fontWeight: 700, color: '#0369a1',
                  background: '#f0f9ff', borderTop: i > 0 ? '1px solid #e2e8f0' : 'none',
                  position: 'sticky', top: 0,
                }}>
                  {o.group}
                </div>
              )}
              <div
                onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                style={{
                  padding: '9px 12px', fontSize: 13, cursor: 'pointer',
                  background: o.value === value ? '#eff6ff' : 'transparent', color: '#0f172a',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = o.value === value ? '#eff6ff' : '#f8fafc'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = o.value === value ? '#eff6ff' : 'transparent'; }}
              >
                {o.label}
                {o.sub && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>{o.sub}</span>}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
